// The "Proeven" tab landing: the one primary CTA (PRD 4.1 -- home is a single
// "Proef een wijn" action) over the contour motif, the group-tasting entries
// as a tile row, and the taster's own history underneath.
import { ListTastingsResponse } from "@proef/contract";
import { useState } from "react";
import {
  Body,
  Button,
  Caption,
  Card,
  Column,
  CountryFlag,
  Display,
  EmptyState,
  ErrorBanner,
  Eyebrow,
  Hero,
  Icon,
  IconButton,
  ListRow,
  PullToRefresh,
  Rise,
  Row,
  Screen,
  Section,
  Fill,
  Skeleton,
  Status,
  Tile,
  type RowAction,
} from "../ui";
import { NoCapture } from "../ui/NoCapture";
import { CachedNote } from "../components/CachedNote";
import { FriendsFeed } from "../components/FriendsFeed";
import { PlannedSessionCard } from "../components/PlannedSessionCard";
import { EdgeOffline } from "../components/EdgeOffline";
import { Glass } from "../components/Glass";
import { UnfinishedList } from "../components/UnfinishedList";
import { POLICY, zodGuard } from "../lib/dataCache";
import { deleteTasting, listTastings } from "../lib/edgeApi";
import { useEdgeStatus } from "../lib/edgeStatus";
import { useLabelScan } from "../lib/features";
import { useT } from "../lib/i18n";
import { navigate } from "../lib/router";
import { progress } from "../lib/tasting/run";
import { useRun } from "../lib/tasting/store";
import { withdrawShareOf } from "../lib/social/tastingSharesApi";
import { useCached } from "../lib/useCached";

const isTastings = zodGuard<ListTastingsResponse>(ListTastingsResponse);

type Tasting = ListTastingsResponse["items"][number];

export function Journal(): React.JSX.Element {
  const { t, locale } = useT();
  const edge = useEdgeStatus();
  const labelScan = useLabelScan();
  const open = useRun();
  // The same test the start screen applies: a finished or fully saved run has nothing to resume.
  const resumable =
    open !== undefined && !open.finished && open.wines.some((wine) => !wine.saved)
      ? open
      : undefined;
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);
  // The journal paints from the phone's copy first; the edge only ever
  // refreshes it, and a saved copy still shows when the edge is down.
  const { data, stale, error, refresh } = useCached(
    "user:tastings:list",
    POLICY.user,
    listTastings,
    isTastings,
    edge !== "down",
  );
  const tastings = data?.items;

  const remove = (tasting: Tasting): void => {
    // oxlint-disable-next-line eslint/no-alert -- a real confirm dialog for a destructive action with no undo.
    if (!window.confirm(t("tasting.confirm-delete.label"))) return;
    setRemoveError(undefined);
    withdrawShareOf(tasting.id)
      .then(() => deleteTasting(tasting.id))
      .then(() => {
        refresh();
        return undefined;
      })
      .catch((err: unknown) => {
        setRemoveError(err instanceof Error ? err.message : t("common.error.generic"));
        return undefined;
      });
  };

  const actionsFor = (tasting: Tasting): { open: RowAction; remove: RowAction } => ({
    open: {
      id: "open",
      label: t("wines.journal.open"),
      icon: "arrow-right",
      onSelect: () => {
        navigate(`/taste/${tasting.id}`);
      },
    },
    remove: {
      id: "remove",
      label: t("tasting.delete.label"),
      icon: "trash",
      tone: "danger",
      onSelect: () => {
        remove(tasting);
      },
    },
  });

  return (
    <Screen>
      <Hero gap="4">
        <Row gap="4">
          <Fill gap="2">
            <Eyebrow color="accent">{t("journal.hero.eyebrow")}</Eyebrow>
            <Display>{t("journal.hero.title")}</Display>
            <Body size="small" tone="dim">
              {t("journal.hero.sub")}
            </Body>
          </Fill>
          <Glass colour="red" width={54} />
        </Row>
        <Button
          full
          icon={<Icon name="scan" size={20} />}
          onPress={() => {
            navigate("/scan?intent=note");
          }}
        >
          {t("home.action.scan")}
        </Button>
        <Caption center fontWeight="600">
          {t(labelScan ? "scan.mode.label" : "scan.mode.codes")}
        </Caption>
      </Hero>

      {resumable !== undefined && (
        <Card accent gap="3" data-testid="journal-resume">
          <Caption weight="strong">{t("tasting.flow.start.resumeTitle")}</Caption>
          <Caption>
            {t("tasting.flow.start.resumeBody", {
              title: resumable.title,
              done: progress(resumable).done,
              total: progress(resumable).total,
            })}
          </Caption>
          <Button
            data-testid="journal-resume-open"
            onPress={() => {
              navigate(
                resumable.started
                  ? `/tasting/wine/${String(resumable.current + 1)}`
                  : "/tasting/lobby",
              );
            }}
          >
            {t("tasting.flow.start.resume")}
          </Button>
        </Card>
      )}

      <Section title={t("journal.quick.title")}>
        <Row gap="3" alignItems="stretch">
          <Rise index={0} flex={1}>
            <Tile
              full
              icon="users"
              label={t("journal.tasting.label")}
              hint={t("together.tile.hint")}
              onPress={() => {
                navigate("/tasting/new");
              }}
            />
          </Rise>
          <Rise index={1} flex={1}>
            <Tile
              full
              icon="eye-off"
              label={t("journal.blind.label")}
              onPress={() => {
                navigate("/tasting/new?blind=1");
              }}
            />
          </Rise>
          <Rise index={2} flex={1}>
            <Tile
              full
              icon="enter"
              label={t("journal.join.label")}
              onPress={() => {
                navigate("/join");
              }}
            />
          </Rise>
        </Row>
      </Section>

      <UnfinishedList />

      <PlannedSessionCard />

      <FriendsFeed />

      <Section
        title={t("journal.recent.title")}
        action={
          <IconButton
            look="ghost"
            aria-label={t("wines.journal.refresh")}
            onPress={() => {
              refresh();
            }}
          >
            <Icon name="refresh" size={18} />
          </IconButton>
        }
      >
        {stale && <CachedNote />}
        {removeError !== undefined && <ErrorBanner message={removeError} />}
        {tastings === undefined && edge === "down" ? (
          <EdgeOffline />
        ) : tastings === undefined && error !== undefined ? (
          <ErrorBanner
            message={error instanceof Error ? error.message : t("common.error.generic")}
          />
        ) : tastings === undefined ? (
          <Column gap="3" aria-busy>
            <Skeleton />
            <Skeleton />
          </Column>
        ) : tastings.length === 0 ? (
          <EmptyState
            icon="book"
            title={t("journal.empty.title")}
            body={t("journal.empty.label")}
          />
        ) : (
          <PullToRefresh
            onRefresh={() => {
              refresh();
              return Promise.resolve();
            }}
          >
            <NoCapture data-testid="journal-list">
              <Card flush>
                {tastings.map((tasting, i) => {
                  const actions = actionsFor(tasting);
                  return (
                    <Rise key={tasting.id} index={i}>
                      <ListRow
                        testId={`journal-row-${tasting.id}`}
                        title={tasting.wine?.name ?? t("blind.mystery-bottle.label")}
                        subtitle={
                          tasting.wine?.producer ??
                          new Date(tasting.tastedAt).toLocaleDateString(locale)
                        }
                        leading={<Glass colour={tasting.wine?.colour ?? "unknown"} />}
                        trailing={
                          <Row gap="2">
                            {tasting.wine !== null && <CountryFlag code={tasting.wine.country} />}
                            {tasting.blind && (
                              <Status tone="lilac">{t("journal.blind-tag.label")}</Status>
                            )}
                            <Icon name="chevron-right" size={18} />
                          </Row>
                        }
                        onPress={actions.open.onSelect}
                        actions={[actions.open, actions.remove]}
                        swipeEnd={actions.remove}
                      />
                    </Rise>
                  );
                })}
              </Card>
            </NoCapture>
          </PullToRefresh>
        )}
      </Section>
    </Screen>
  );
}
